import Vue from 'vue';
import VueRouter, {RouteConfig, Route, RawLocation} from 'vue-router';
import {RouterAliases} from './routerAliases';
import {idb} from '@/store/persistent-store';
import {resolveAppRoute} from '@components/router-resolver';
import {Dictionary, Position} from 'vue-router/types/router';
import {computed} from '@vue/composition-api';
import {registerStoreMainModule} from '@/store/state/helpers';

const originalPush = VueRouter.prototype.push;
VueRouter.prototype.push = function push(location: RawLocation): Promise<Route> {
  return new Promise((resolve, reject) => {
    originalPush.call(
      this,
      location,
      () => {
        // on complete

        resolve(this.currentRoute);
      },
      (error) => {
        // on abort

        // only ignore NavigationDuplicated error
        if (error.name === 'NavigationDuplicated') {
          resolve(this.currentRoute);
        } else {
          reject(error);
        }
      },
    );
  });
};

Vue.use(VueRouter);

const routes: RouteConfig[] = [
  {
    path: '/home',
    name: RouterAliases.AMEX_LANDING,
    component: () => import(/* amex-landing*/ '@components/landing/amex-landing.vue'),
  },
  {
    path: '/landing',
    name: RouterAliases.AMEX_LANDING2,
    component: () => import(/* amex-landing*/ '@components/landing/amex-landing.vue'),
  },
  {
    path: '/callback',
    name: RouterAliases.AUTH_CALLBACK,
    component: () => import(/* decision-maker-component*/ '@/views/Auth0Callback.vue'),
  },
  {
    path: '',
    name: RouterAliases.BASE_VIEW,
    component: () => import(/* webpackChunkName: "amex-base-empty" */ '../views/amex/base-view.vue'),
    children: [
      {
        path: '/discovery',
        name: RouterAliases.DISCOVERY_VIEW,
        component: () =>
          import(/* webpackChunkName: "home-page" */ '../views/amex/components/discovery-page/discovery-page.vue'),
      },
      {
        path: '/my-trips',
        name: RouterAliases.MY_TRIPS_VIEW,
        component: () => import('../views/amex/components/my-trips/my-trips.vue'),
      },
      {
        path: '/profile',
        name: RouterAliases.AMEX_PROFILE,
        component: () => import('../views/amex/components/profile-page/profile-page.vue'),
      },
      {
        path: '/summary/:tripId/:contextName',
        name: RouterAliases.SUMMARY_VIEW,
        component: () =>
          import(/* webpackChunkName: "summary-page" */ '../views/amex/components/summary-page/summary-page.vue'),
      },
      {
        path: '/pdf/:tripId/:contextName',
        name: RouterAliases.EXPORT_PDF_VIEW,
        component: () =>
          import(/* webpackChunkName: "export-pdf" */ '../views/amex/components/export-pdf/export-pdf.vue'),
      },

      {
        path: '/explore/:tripId/:contextName',
        redirect: '/explore/:tripId/:contextName/ACCOMMODATION',
        component: () =>
          import(/* webpackChunkName: "amex-explore" */ '../views/amex/components/explore-page/explore-page.vue'),
        children: [
          {
            path: 'FLIGHT',
            name: RouterAliases.FLIGHT_EXPLORE_VIEW,
            component: () =>
              import(
                /* webpackChunkName: "flight-explore" */ '../views/amex/components/explore-page/flight-explore/flight-details.vue'
              ),
          },
          {
            path: 'ACCOMMODATION',
            name: RouterAliases.ACCOMMODATION_EXPLORE_VIEW,
            component: () =>
              import(
                /* webpackChunkName: "accommodation-explore" */ '../views/amex/components/explore-page/stay-explore/stay-explore.vue'
              ),
          },
          {
            path: 'EXPERIENCE',
            name: RouterAliases.EXPERIENCE_EXPLORE_VIEW,
            component: () =>
              import(
                /* webpackChunkName: "experience-explore" */ '../views/amex/components/explore-page/exp-explore/exp-explore.vue'
              ),
          },
          {
            path: 'DINING',
            name: RouterAliases.DINNING_EXPLORE_VIEW,
            component: () =>
              import(
                /* webpackChunkName: "dining-explore" */ '../views/amex/components/explore-page/dining-explore/dining-explore.vue'
              ),
          },
          // {
          //   path: 'SAVED',
          //   name: RouterAliases.SAVED_EXPLORE_VIEW,
          //   component: () =>
          //     import(
          //       /* webpackChunkName: "saved-explore" */ '../views/amex/components/explore-page/saved-explore/saved-explore.vue'
          //     ),
          // },
          {
            path: 'details/:poiId',
            name: RouterAliases.POI_DETAILS_VIEW,
            component: () =>
              import(
                /* webpackChunkName: "details-explore" */ '../views/amex/components/explore-page/details-view/details-view.vue'
              ),
          },
        ],
      },
    ],
  },
];

const router = new VueRouter({
  mode: 'history',
  base: process.env.VUE_APP_BASE_URL,
  routes,
  scrollBehavior(to: Route, from: Route, savedPosition: void | Position) {
    if (to.name === from.name) {
      return savedPosition;
    }
    return {x: 0, y: 0};
  },
});

router.beforeEach(async (to, from, next) => {
  const appVersion = process.env.VUE_APP_VERSION;
  const oldVersion = await idb.getItem('appVersion');
  if (oldVersion !== appVersion) {
    // store.commit('reset');
    await idb.clear();
    await idb.setItem('appVersion', appVersion);
    const start = process.env.VUE_APP_BASE_URL;
    const path = `${start}/${to.fullPath}`.replace(/\/+/g, '/');
    window.location.href = path;
  }
  if (to.name === RouterAliases.AUTH_CALLBACK) {
    return next();
  }
  if (to.name === RouterAliases.AMEX_LANDING) {
    return next();
  }
  if (to.name === RouterAliases.AMEX_LANDING2) {
    return next();
  }
  if (to.name === RouterAliases.EXPORT_PDF_VIEW) {
    await registerStoreMainModule();
    return next();
  }

  if (await resolveAppRoute(to, from, next)) {
    return;
  }
  return next();
});

const routeData = Vue.observable<{
  query: Dictionary<string | (string | null)[]>;
  params: Dictionary<string>;
  path: string;
  name: string;
}>({
  params: {},
  query: {},
  path: '',
  name: '',
});

router.afterEach((route) => {
  routeData.params = route.params;
  routeData.query = route.query;
  routeData.path = route.path;
  routeData.name = route.name || '';
});

export function useParams() {
  return computed(() => routeData.params);
}

export function useRouterData() {
  return computed(() => ({name: routeData.name, path: routeData.path}));
}

export function useQueryParams() {
  return computed(() => routeData.query);
}

export default router;
